import type {
  CountryCode,
  LibraryTabDraft,
  LibraryTabItem,
  PlatformId,
  ProductId,
} from './types'
import { LIBRARY_TAB_RC_CONFIG } from './rcConfig'

export const PRODUCTS: { id: ProductId; label: string }[] = [
  { id: 'neku', label: 'Neku' },
]

export const PLATFORMS: { id: PlatformId; label: string }[] = [
  { id: 'android', label: 'Android' },
  { id: 'ios', label: 'iOS' },
]

/** RC parameter default value (no country condition) */
export const DEFAULT_SCOPE: CountryCode = 'DEFAULT'

export const DEFAULT_SCOPE_LABEL = '通用'

/** @deprecated use LIBRARY_TAB_RC_CONFIG.parameterKey */
export const RC_PARAMETER_KEY = LIBRARY_TAB_RC_CONFIG.parameterKey


export type CountryMeta = {
  code: CountryCode
  label: string
}

/** Regular (non-AB / non-version) countries per platform */
export const COUNTRIES_BY_PLATFORM: Record<PlatformId, CountryMeta[]> = {
  android: [
    { code: 'US', label: 'US' },
    { code: 'JP', label: 'JP' },
    { code: 'KR', label: 'KR' },
    { code: 'HK-TW', label: 'HK-TW' },
    { code: 'TH', label: 'TH' },
    { code: 'MX', label: 'MX' },
  ],
  ios: [
    { code: 'DE', label: 'DE' },
    { code: 'BR', label: 'BR' },
    { code: 'JP', label: 'JP' },
    { code: 'CA', label: 'CA' },
    { code: 'MX', label: 'MX' },
  ],
}

/** Firebase RC condition name mapping (local preview) */
export const CONDITION_BY_PRODUCT_PLATFORM_COUNTRY: Record<
  ProductId,
  Record<PlatformId, Partial<Record<CountryCode, string>>>
> = {
  neku: {
    android: {
      US: 'Neku_Android_US',
      JP: 'Neku_Android_JP',
      KR: 'Neku_Android_KR',
      'HK-TW': 'Neku_Android_HK-TW',
      TH: 'Neku_Android_TH',
      MX: 'Neku_Android_MX',
    },
    ios: {
      DE: 'Neku_IOS_DE',
      BR: 'Neku_IOS_BR',
      JP: 'Neku_IOS_JP',
      CA: 'Neku_IOS_CA',
      MX: 'Neku_IOS_MX',
    },
  },
}

export function draftKey(product: ProductId, platform: PlatformId): string {
  return `${product}:${platform}`
}

function newId(): string {
  return `tab_${Math.random().toString(36).slice(2, 10)}`
}

function item(
  tag: string,
  titles: Partial<Record<CountryCode, string>>,
  visibleIn: CountryCode[],
): LibraryTabItem {
  return { id: newId(), tag, titles, visibleIn }
}

function seedAndroid(): LibraryTabDraft {
  const all: CountryCode[] = ['US', 'JP', 'KR', 'HK-TW', 'TH', 'MX']
  const latam: CountryCode[] = ['US', 'MX']
  const items: LibraryTabItem[] = [
    item(
      'dailyTop',
      {
        [DEFAULT_SCOPE]: 'Daily Top',
        US: 'Daily Top',
        JP: 'デイリー',
        KR: '일일 인기',
        'HK-TW': '每日熱門',
        TH: 'อันดับรายวัน',
        MX: 'Top Diario',
      },
      [DEFAULT_SCOPE, ...all],
    ),
    item(
      'trending',
      {
        [DEFAULT_SCOPE]: 'Trending',
        US: 'Trending',
        JP: 'Trending',
        KR: 'Trending',
        'HK-TW': 'Trending',
        TH: 'Trending',
        MX: 'Trending',
      },
      [DEFAULT_SCOPE, ...all],
    ),
    item(
      '🏆World Cup',
      {
        [DEFAULT_SCOPE]: '🏆World Cup',
        US: '🏆World Cup',
        MX: '🏆World Cup',
      },
      [DEFAULT_SCOPE, ...latam],
    ),
    item(
      'Beautiful girl',
      {
        [DEFAULT_SCOPE]: 'Beautiful',
        US: 'Beautiful',
        MX: 'Beautiful',
      },
      [DEFAULT_SCOPE, ...latam],
    ),
    item(
      'Everyday/Urban',
      {
        [DEFAULT_SCOPE]: 'Everyday/Urban',
        US: 'Everyday/Urban',
        MX: 'Everyday/Urban',
      },
      [DEFAULT_SCOPE, ...latam],
    ),
    item(
      'Female Characters',
      {
        [DEFAULT_SCOPE]: 'Female Characters',
        US: 'Female Characters',
        MX: 'Female Characters',
      },
      [DEFAULT_SCOPE, ...latam],
    ),
    item(
      'Vibrant Colors',
      {
        [DEFAULT_SCOPE]: 'Vibrant Colors',
        US: 'Vibrant Colors',
        MX: 'Vibrant Colors',
      },
      [DEFAULT_SCOPE, ...latam],
    ),
    item(
      'Funny',
      {
        [DEFAULT_SCOPE]: 'Funny',
        US: 'Funny',
        MX: 'Funny',
      },
      [DEFAULT_SCOPE, ...latam],
    ),
  ]

  return {
    product: 'neku',
    platform: 'android',
    countries: [...all],
    conditionNames: {},
    items,
    countryOrders: {},
  }
}

function seedIos(): LibraryTabDraft {
  const all: CountryCode[] = ['DE', 'BR', 'JP', 'CA', 'MX']
  const items: LibraryTabItem[] = [
    item(
      'dailyTop',
      {
        [DEFAULT_SCOPE]: 'Daily Top',
        DE: 'Tägliche Top',
        BR: 'Top Diário',
        JP: 'デイリー',
        CA: 'Daily Top',
        MX: 'Top Diario',
      },
      [DEFAULT_SCOPE, ...all],
    ),
    item(
      'trending',
      {
        [DEFAULT_SCOPE]: 'Trending',
        DE: 'Trending',
        BR: 'Trending',
        JP: 'Trending',
        CA: 'Trending',
        MX: 'Trending',
      },
      [DEFAULT_SCOPE, ...all],
    ),
  ]

  return {
    product: 'neku',
    platform: 'ios',
    countries: [...all],
    conditionNames: {},
    items,
    countryOrders: {},
  }
}

export function defaultConditionName(
  product: ProductId,
  platform: PlatformId,
  country: CountryCode,
  isVersion?: boolean | null,
): string {
  const os = platform === 'ios' ? 'IOS' : 'Android'
  const productLabel = product === 'neku' ? 'Neku' : product
  const suffix = LIBRARY_TAB_RC_CONFIG.conditionSuffix
  const versionSuffix = isVersion ? '_Version' : ''
  if (country === DEFAULT_SCOPE) {
    return `${productLabel}_${os}${suffix}${versionSuffix}`
  }
  return `${productLabel}_${os}_${country}${suffix}${versionSuffix}`
}

export function scopeLabel(code: CountryCode): string {
  if (code === DEFAULT_SCOPE) return DEFAULT_SCOPE_LABEL
  return code
}

/** Common country codes operators may add (beyond current columns) */
export const COUNTRY_PRESETS: CountryCode[] = [
  'US',
  'JP',
  'KR',
  'MX',
  'BR',
  'DE',
  'CA',
  'HK-TW',
  'TH',
  'ID',
  'IN',
  'TW',
  'HK',
  'SG',
  'MY',
  'PH',
  'VN',
  'GB',
  'FR',
  'ES',
  'IT',
  'AU',
  'AR',
  'CO',
  'TR',
  'RU',
]

export function normalizeCountryCode(raw: string): string {
  return raw.trim().replace(/\s+/g, '').toUpperCase().replace(/_/g, '-')
}

export function createSeedDraft(
  product: ProductId,
  platform: PlatformId,
): LibraryTabDraft {
  if (product === 'neku' && platform === 'android') return seedAndroid()
  if (product === 'neku' && platform === 'ios') return seedIos()
  return {
    product,
    platform,
    countries: COUNTRIES_BY_PLATFORM[platform].map((c) => c.code),
    conditionNames: {},
    items: [],
    countryOrders: {},
  }
}

export function createEmptyItem(countries: CountryCode[]): LibraryTabItem {
  const scopes = Array.from(new Set([DEFAULT_SCOPE, ...countries]))
  return {
    id: newId(),
    tag: '',
    titles: {},
    visibleIn: scopes,
  }
}

/** Column order: these countries first, then the rest A–Z */
export const COUNTRY_COLUMN_PRIORITY: CountryCode[] = ['US', 'BR', 'MX']

export function sortCountryColumns(countries: CountryCode[]): CountryCode[] {
  const priority = new Map(
    COUNTRY_COLUMN_PRIORITY.map((code, i) => [code.toUpperCase(), i]),
  )
  return [...countries].sort((a, b) => {
    const pa = priority.get(a.toUpperCase())
    const pb = priority.get(b.toUpperCase())
    if (pa != null && pb != null) return pa - pb
    if (pa != null) return -1
    if (pb != null) return 1
    return a.localeCompare(b)
  })
}

export function matrixScopes(countries: CountryCode[]): CountryCode[] {
  const rest = sortCountryColumns(
    countries.filter((c) => c !== DEFAULT_SCOPE),
  )
  return [DEFAULT_SCOPE, ...rest]
}

export function isDefaultScope(code: CountryCode): boolean {
  return code === DEFAULT_SCOPE
}

export function isFixedScope(code: CountryCode): boolean {
  return code === DEFAULT_SCOPE
}
